import type { RoiId } from '../changes/ids';
import { RoiReadModel } from '../readmodels/StructureSetReadModel';

import { Grid } from '../../engine/data/common/Grid';
import { compressSlices } from '../sdfCompression';

export interface RoiMemento {
    readonly roi: RoiReadModel;
    readonly compressedContourPixels?: Uint8Array;
    readonly grid?: Grid;
    readonly timestamp: Date;
}

export class ReversibleOperation {
    constructor(
        private readonly state: RoiMemento,
        private readonly description: string,
        /**
         * The raw operation label as recorded when the step was pushed
         * ('margin', 'draw', 'initial-state'). Kept separate from
         * {@link description}, which embeds the ROI name: the
         * history list shows what was done, and parsing it back out of the
         * prose would break the moment either is reworded. Defaults to the
         * description so pre-existing two-argument callers keep compiling.
         */
        private readonly operation: string = description
    ) {}

    getDescription(): string {
        return this.description;
    }

    getOperation(): string {
        return this.operation;
    }

    getTimestamp(): Date {
        return this.state.timestamp;
    }

    getState(): RoiMemento {
        return this.state;
    }

    getRoiId(): RoiId {
        return this.state.roi.roiId;
    }

    getStructureSetUid(): string {
        return this.state.roi.structureSetUid;
    }

    hasContourChanges(): boolean {
        return !!this.state.compressedContourPixels;
    }

    getAffectedEntities(): Array<{
        type: 'roi' | 'structureSet' | 'global';
        id: string;
    }> {
        return [
            { type: 'roi', id: this.state.roi.roiId },
            { type: 'structureSet', id: 'current-structure-set' },
        ];
    }
}

/**
 * Factory for creating ROI mementos from RoiReadModel
 */
export class RoiMementoFactory {
    /**
     * Create memento from RoiReadModel
     */
    static fromReadModel(roi: RoiReadModel): RoiMemento {
        return {
            roi, // Already immutable, just store it
            timestamp: new Date(),
        };
    }

    /**
     * Create memento with full contour data.
     * Compression runs in a Web Worker via fflate.compress()
     * to avoid blocking the main thread.
     */
    static async fromReadModelWithContours(
        roi: RoiReadModel,
        contourPixelSlices: Uint8Array[],
        grid: Grid
    ): Promise<RoiMemento> {
        const compressedPixels = await compressSlices(contourPixelSlices);

        return {
            roi,
            compressedContourPixels: compressedPixels,
            grid,
            timestamp: new Date(),
        };
    }
}
